import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Select,
  useToast,
  Grid,
  Heading,
  InputGroup,
  InputRightAddon,
} from "@chakra-ui/react";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { fetchRoles } from "../../actions/role.actions";
import { createNewUser } from "../../actions/users.action";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Unauthorized } from "../../components/Unauthorized";
import { useContext } from "react";
import { AuthContext } from "../../providers/AuthValidationLayer";
import { useLocation, useNavigate } from "react-router-dom";
import { Select as ChakraSelect } from "chakra-react-select";
import axios from "axios";
import { useRef } from "react";
// Auth header helper
const getAuthHeaders = () => {
  const token = localStorage.getItem("prima_session");
  if (!token) {
    throw new Error("Token not found");
  }
  return {
    Authorization: `Bearer ${token.replace(/^Bearer\s+/i, "").replace(/['"]/g, "")}`,
  };
};
const CreateAdmin = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user } = useContext(AuthContext);
  const source = new URLSearchParams(location.search).get("source");
  const isFromManageEmployees = source === "manage-employees";

  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ ADMIN SCHEMA ALIGNED STATE

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    roleId: "",
    username: "",
    employeeId: "",
    languageKnown: [],
  });

  const [addressProofPreview, setAddressProofPreview] = useState(null);
  const [idProofPreview, setIdProofPreview] = useState(null);
  const addressProofInputRef = useRef();
  const idProofInputRef = useRef();
  const [addressProofFile, setAddressProofFile] = useState(null);
  const [idProofFile, setIdProofFile] = useState(null);

  const handleFileChange = (e, setPreview, setFile) => {
    const file = e.target.files[0];
    if (!file) return;

    setFile(file); // ✅ Store real file

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setPreview({ type: "image", src: ev.target.result, name: file.name });
      reader.readAsDataURL(file);
    } else if (file.type === "application/pdf") {
      setPreview({ type: "pdf", src: URL.createObjectURL(file), name: file.name });
    } else {
      setPreview({ type: "other", name: file.name });
    }
  };
  // Filter roles based on current user's permissions
  const getFilteredRoles = (allRoles) => {
    // If user is superadmin, show all roles
    if (user?.roleId?.roleName?.toLowerCase() === "superadmin") {
      return allRoles;
    }

    const currentUserAccess = user?.roleId?.roleAccess || [];
    const currentUserAccessSections = currentUserAccess.map((section) =>
      section.heading?.toLowerCase(),
    );

    // Filter roles: only show roles that don't have sections beyond user's access
    return allRoles.filter((role) => {
      if (role.roleAccess?.length === 0) return true; // Allow roles with no restrictions

      // Check if all role's sections are within user's access
      return role.roleAccess?.every((roleSection) =>
        currentUserAccessSections.includes(roleSection.heading?.toLowerCase()),
      );
    });
  };

  // ---------------- handlers ----------------
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    // For date fields, always save as string (yyyy-mm-dd)
    if (type === 'date') {
      setFormData((prev) => ({ ...prev, [name]: value ? value : '' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const getAllRoles = async () => {
    try {
      const data = await fetchRoles();
      const allRoles = data || [];
      setRoles(allRoles);
      setFilteredRoles(getFilteredRoles(allRoles));
    } catch (err) {
      setError(err?.message || "Unable to fetch roles");
    }
  };

  // Validation for all fields
  const validateForm = (data) => {
    // Required fields (all marked isRequired in UI)
    const requiredFields = [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'password', label: 'Password' },
      { key: 'roleId', label: 'Role' },
      { key: 'mobileNumber', label: 'Mobile Number' },
      { key: 'dateOfBirth', label: 'Date of Birth' },
      { key: 'gender', label: 'Gender' },
      { key: 'department', label: 'Department' },
      { key: 'designation', label: 'Designation' },
      { key: 'dateOfJoining', label: 'Date of Joining' },
      { key: 'branch', label: 'Branch' },
      { key: 'emergencyContactName', label: 'Emergency Contact Name' },
      { key: 'emergencyContactRelation', label: 'Emergency Contact Relation' },
      { key: 'emergencyContactNumber', label: 'Emergency Contact Number' },
      { key: 'userAddress', label: 'User Address' },
      { key: 'state', label: 'State' },
      { key: 'city', label: 'City' },
      { key: 'pincode', label: 'Pincode' },
    ];
    for (const field of requiredFields) {
      if (
        !data[field.key] ||
        (typeof data[field.key] === 'string' && !data[field.key].trim()) ||
        (typeof data[field.key] === 'string' && /^\s+$/.test(data[field.key]))
      ) {
        return `${field.label} is required and cannot be only whitespace`;
      }
    }
    // Check all string fields for only whitespace
    for (const key in data) {
      if (
        typeof data[key] === 'string' &&
        /^\s+$/.test(data[key])
      ) {
        return `${key.charAt(0).toUpperCase() + key.slice(1)} cannot be only whitespace`;
      }
    }
    // Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (data.email && (!emailRegex.test(data.email) || data.email.length > 128)) {
      return 'Please enter a valid email (max 128 characters)';
    }
    // Password
    if (data.password && (data.password.length < 6 || data.password.length > 20)) {
      return 'Password must be 6-20 characters';
    }
    // Name
    if (data.name && (data.name.length > 100 || !/^[a-zA-Z0-9 ]+$/.test(data.name))) {
      return 'Name cannot exceed 100 characters or contain special characters';
    }
    // Mobile
    if (data.mobileNumber && !/^[6-9]\d{9}$/.test(data.mobileNumber)) {
      return 'Mobile Number must be a valid 10-digit Indian number';
    }
    // Pincode
    if (data.pincode && !/^[1-9][0-9]{5}$/.test(data.pincode)) {
      return 'Pincode must be a valid 6-digit number';
    }
    // Emergency Contact Number
    if (data.emergencyContactNumber && !/^[6-9]\d{9}$/.test(data.emergencyContactNumber)) {
      return 'Emergency Contact Number must be a valid 10-digit Indian number';
    }
    return null;
  };

  const handleLanguageChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      languageKnown: selected ? selected.map((opt) => opt.value) : [],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Merge all form fields (original + new)
    const allFields = { ...formData };
    const form = e.target;
    // Add new fields from the form
    [
      'mobileNumber', 'dateOfBirth', 'gender', 'enablePasswordReset', 'department', 'team', 'designation', 'dateOfJoining', 'branch', 'shiftTimings', 'emergencyContactName', 'emergencyContactRelation', 'emergencyContactNumber', 'userAddress', 'state', 'city', 'pincode'
    ].forEach((key) => {
      if (form[key]) {
        if (form[key].type === 'checkbox') {
          allFields[key] = form[key].checked;
        } else if (form[key].type === 'date') {
          // Always save as yyyy-mm-dd string
          allFields[key] = form[key].value ? form[key].value : '';
        } else {
          allFields[key] = form[key].value;
        }
      }
    });

    const validationError = validateForm(allFields);
    if (validationError) {
      toast({ title: 'Validation Error', description: validationError, status: 'warning' });
      setLoading(false);
      return;
    }

    // Map userAddress to address for backend compatibility
    allFields.address = allFields.userAddress;
    delete allFields.userAddress;

    // Clean up languageKnown: remove empty strings and ensure array is valid
    if (Array.isArray(allFields.languageKnown)) {
      allFields.languageKnown = allFields.languageKnown.filter(
        (lang) => typeof lang === 'string' && lang.trim() !== ''
      );
    } else if (typeof allFields.languageKnown === 'string') {
      allFields.languageKnown = allFields.languageKnown.trim() ? [allFields.languageKnown.trim()] : [];
    } else {
      allFields.languageKnown = [];
    }

    try {
      const formPayload = new FormData();

      // Append all normal fields
      Object.keys(allFields).forEach((key) => {
        if (key === "languageKnown") {
          formPayload.append(key, JSON.stringify(allFields[key])); // ✅ stringify array
        } else {
          formPayload.append(key, allFields[key]);
        }
      });

      // Append trimmed values
      formPayload.set("name", allFields.name.trim());
      formPayload.set("email", allFields.email.trim());

      // Append files
      if (addressProofFile) {
        formPayload.append("addressProofFiles", addressProofFile);
      }

      if (idProofFile) {
        formPayload.append("idProofFiles", idProofFile);
      }

      await createNewUser(formPayload);

      toast({
        title: "Success",
        description: "Employee created successfully",
        status: "success",
      });

      setFormData({
        name: "",
        email: "",
        password: "",
        roleId: "",
        username: "",
        employeeId: "",
      });

      navigate("/settings/admin/manage");
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to create admin",
        status: "error",
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!formData.email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return;

    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${import.meta.env.VITE_API_URL}/admin/new?preview=true`,
          {
            email: formData.email,
            password: "preview",
            name: "preview",
            roleId: formData.roleId || "preview"
          },
          {
            headers: getAuthHeaders(),
          }
        );

        const data = res.data;

        if (data.success) {
          setFormData(prev => ({
            ...prev,
            username: data.data.username,
            employeeId: data.data.employeeId
          }));
        }
      } catch (err) {
        console.log("Preview generation failed", err.response?.data);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.email]);

  useEffect(() => {
    getAllRoles();
  }, []);

  if (!hasPermission("Access Control", "Employees", "POST")) return <Unauthorized />;
  if (error === "Unauthorized user") return <Unauthorized />;

  return (
    <Box p={6}>
      <BreadCrumbMaker
        items={
          isFromManageEmployees
            ? [
              { link: "/settings", title: "Access Control" },
              { link: "/settings/admin/manage", title: "Manage Employees" },
              {
                link: "/settings/admin/create?source=manage-employees",
                title: "Create Employee",
                on: true,
              },
            ]
            : [
              { link: "/settings", title: "Access Control" },
              { link: "/settings/admin/create", title: "Create Employee", on: true },
            ]
        }
      />

      <Box bg="white" p={6} borderRadius="2xl" mt={4}>
        <Heading size="md" mb={6}>
          Create Employee
        </Heading>
        <form onSubmit={handleSubmit}>
          {/* The original 4 fields */}
          <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }} gap={4} mb={8}>
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full name"
                maxLength={55}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@email.com"
                maxLength={128}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  maxLength={20}
                />
                <InputRightAddon
                  cursor="pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </InputRightAddon>
              </InputGroup>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontWeight="600" color="gray.700">
                Role
              </FormLabel>
              <ChakraSelect
                placeholder="Select a role"
                size="lg"
                options={filteredRoles}
                value={filteredRoles.find((r) => r._id === formData.roleId) || null}
                getOptionLabel={(option) => option.roleName}
                getOptionValue={(option) => option._id}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    roleId: selected?._id || "",
                  }))
                }
                chakraStyles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "8px",
                    borderColor: "#CBD5E0",
                    _hover: { borderColor: "#3182CE" },
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 10,
                  }),
                }}
              />
            </FormControl>
          </Grid>

          {/* Additional fields grouped as per UI */}
          {/* User Details (rest) */}
          <Box mb={8} borderWidth={1} borderRadius="lg" p={4}>
            <Heading size="sm" mb={4}>User Details</Heading>
            <Grid templateColumns={{ base: "1fr", md: "repeat(4,1fr)" }} gap={4}>
              <FormControl isRequired><FormLabel>Mobile Number</FormLabel><Input name="mobileNumber" placeholder="Enter Mobile Number" type="tel" pattern="[6-9]{1}[0-9]{9}" maxLength={10} inputMode="numeric" /></FormControl>
              <FormControl isRequired><FormLabel>Date of Birth</FormLabel>
               <Input 
                name="dateOfBirth" 
                type="date" 
                max={new Date().toISOString().split("T")[0]} 
               /></FormControl>
              <FormControl isRequired><FormLabel>Gender</FormLabel><Select name="gender" placeholder="Select Gender"><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></Select></FormControl>
            </Grid>
          </Box>

          {/* Employment Details */}
          <Box mb={8} borderWidth={1} borderRadius="lg" p={4}>
            <Heading size="sm" mb={4}>Employment Details</Heading>
            <Grid templateColumns={{ base: "1fr", md: "repeat(4,1fr)" }} gap={4}>
              <FormControl>
                <FormLabel>Username/ID</FormLabel>
                <Input
                  name="username"
                  value={formData.username}
                  placeholder="Autogenerated"
                  isReadOnly
                />
              </FormControl>
              <FormControl>
                <FormLabel>Employee ID</FormLabel>
                <Input
                  name="employeeId"
                  value={formData.employeeId}
                  placeholder="Autogenerated"
                  isReadOnly
                />
              </FormControl>
              <FormControl isRequired><FormLabel>Department</FormLabel>
                <Select name="department" placeholder="Select Department">
                  <option value="HR">HR</option>
                  <option value="Sales">Sales</option>
                  <option value="Operations">Operations</option>
                  <option value="Finance">Finance</option>
                  <option value="IT">IT</option>
                  <option value="Admin">Admin</option>
                  <option value="Support">Support</option>
                  <option value="Other">Other</option>
                </Select>
              </FormControl>
              <FormControl><FormLabel>Team</FormLabel><Input name="team" placeholder="Team name" /></FormControl>
              <FormControl isRequired><FormLabel>Designation</FormLabel><Input name="designation" placeholder="Enter Designation" /></FormControl>
              <FormControl isRequired>
                <FormLabel>Date of Joining</FormLabel>
                <Input
                  name="dateOfJoining"
                  type="date"
                  placeholder="Enter dateOfJoining"
                  value={formData.dateOfJoining || ''}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl isRequired><FormLabel>Branch</FormLabel><Input name="branch" placeholder="Enter Branch Name" /></FormControl>
              <FormControl><FormLabel>Shift Timmings</FormLabel><Input name="shiftTimings" placeholder="Eg : 9 - 6 pm" /></FormControl>
            </Grid>
          </Box>

          {/* Emergency Contact & Languages */}
          <Box mb={8} borderWidth={1} borderRadius="lg" p={4}>
            <Heading size="sm" mb={4}>Emergency Contact & Languages</Heading>
            <Grid templateColumns={{ base: "1fr", md: "repeat(4,1fr)" }} gap={4}>
              <FormControl isRequired><FormLabel>Emergency Contact Name</FormLabel><Input name="emergencyContactName" placeholder="Enter Name" /></FormControl>
              <FormControl isRequired><FormLabel>Emergency Contact Relation</FormLabel><Input name="emergencyContactRelation" placeholder="Relation with user" /></FormControl>
              <FormControl isRequired><FormLabel>Emergency Contact Number</FormLabel>
               <Input 
                name="emergencyContactNumber" 
                placeholder="Enter Number" 
                type="tel"
                pattern="[6-9]{1}[0-9]{9}"
                maxLength={10}
                inputMode="numeric"
                onInput={(e) => {e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10); }}
                /></FormControl>
              <FormControl>
                <FormLabel>Language Known</FormLabel>
                <ChakraSelect
                  isMulti
                  name="languageKnown"
                  placeholder="Select Language(s)"
                  options={[
                    { value: "English", label: "English" },
                    { value: "Hindi", label: "Hindi" },
                    { value: "Marathi", label: "Marathi" },
                    { value: "Gujarati", label: "Gujarati" },
                    { value: "Tamil", label: "Tamil" },
                    { value: "Telugu", label: "Telugu" },
                    { value: "Kannada", label: "Kannada" },
                    { value: "Bengali", label: "Bengali" },
                    { value: "Punjabi", label: "Punjabi" },
                    { value: "Other", label: "Other" },
                  ]}
                  value={formData.languageKnown.map((lang) => ({
                    value: lang,
                    label: lang,
                  }))}
                  onChange={handleLanguageChange}
                  closeMenuOnSelect={false}
                  chakraStyles={{
                    control: (base) => ({ ...base, borderRadius: "8px", borderColor: "#CBD5E0", _hover: { borderColor: "#3182CE" } }),
                    menu: (base) => ({ ...base, zIndex: 10 }),
                  }}
                />
              </FormControl>
            </Grid>
          </Box>

          {/* Address & Documents */}
          <Box mb={8} borderWidth={1} borderRadius="lg" p={4}>
            <Heading size="sm" mb={4}>Address & Documents</Heading>
            <Grid templateColumns={{ base: "1fr", md: "repeat(4,1fr)" }} gap={4}>
              <FormControl isRequired><FormLabel>User Address</FormLabel><Input name="userAddress" placeholder="Enter Full Address" /></FormControl>
              <FormControl isRequired><FormLabel>State</FormLabel><Input name="state" placeholder="Enter State" /></FormControl>
              <FormControl isRequired><FormLabel>City</FormLabel><Input name="city" placeholder="Enter City" /></FormControl>
              <FormControl isRequired><FormLabel>Pincode</FormLabel><Input name="pincode" placeholder="Enter Pincode" /></FormControl>
            </Grid>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }} gap={4} mt={4}>
              <Box borderWidth={1} borderRadius="lg" p={4}>
                <Heading size="xs" mb={2}>Upload Address Proof</Heading>
                <Box borderWidth={1} borderRadius="md" p={4} textAlign="center" minH="120px" color="gray.500">
                  Drag your file(s) to start uploading<br />or<br />
                  <Button mt={2} size="sm" onClick={() => addressProofInputRef.current.click()}>Browse files</Button>
                  <input
                    id="addressProofInput"
                    name="addressProof"
                    type="file"
                    accept=".jpg,.png,.svg,.zip,.pdf"
                    style={{ display: 'none' }}
                    ref={addressProofInputRef}
                    onChange={e => handleFileChange(e, setAddressProofPreview, setAddressProofFile)}
                  />
                  <Box fontSize="xs" mt={2}>Only support .jpg, .png, .svg, .pdf and zip files</Box>
                  {addressProofPreview && (
                    <Box mt={2}>
                      <Box fontWeight="bold">{addressProofPreview.name}</Box>
                      {addressProofPreview.type === "image" && (
                        <img src={addressProofPreview.src} alt="Preview" style={{ maxWidth: 120, maxHeight: 120, margin: '0 auto' }} />
                      )}
                      {addressProofPreview.type === "pdf" && (
                        <Box>
                          <embed src={addressProofPreview.src} type="application/pdf" width="120" height="120" />
                        </Box>
                      )}
                      {addressProofPreview.type === "other" && (
                        <Box color="gray.400">Preview not available</Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
              <Box borderWidth={1} borderRadius="lg" p={4}>
                <Heading size="xs" mb={2}>Upload ID</Heading>
                <Box borderWidth={1} borderRadius="md" p={4} textAlign="center" minH="120px" color="gray.500">
                  Drag your file(s) to start uploading<br />or<br />
                  <Button mt={2} size="sm" onClick={() => idProofInputRef.current.click()}>Browse files</Button>
                  <input
                    id="idProofInput"
                    name="idProof"
                    type="file"
                    accept=".jpg,.png,.svg,.zip,.pdf"
                    style={{ display: 'none' }}
                    ref={idProofInputRef}
                    onChange={e => handleFileChange(e, setIdProofPreview, setIdProofFile)}
                  />
                  <Box fontSize="xs" mt={2}>Only support .jpg, .png, .svg, .pdf and zip files</Box>
                  {idProofPreview && (
                    <Box mt={2}>
                      <Box fontWeight="bold">{idProofPreview.name}</Box>
                      {idProofPreview.type === "image" && (
                        <img src={idProofPreview.src} alt="Preview" style={{ maxWidth: 120, maxHeight: 120, margin: '0 auto' }} />
                      )}
                      {idProofPreview.type === "pdf" && (
                        <Box>
                          <embed src={idProofPreview.src} type="application/pdf" width="120" height="120" />
                        </Box>
                      )}
                      {idProofPreview.type === "other" && (
                        <Box color="gray.400">Preview not available</Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Box>

          <Button mt={6} colorScheme="blue" type="submit" isLoading={loading} loadingText="Saving...">
            Create Employee
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default CreateAdmin;
